import dayjs from 'dayjs';
import { createContext, useContext } from 'react';
import type { SeedRecommendationStep, SeedRecommendationType } from '/@/store/tracking/events/seed-recommendation';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';

/**
 * What the wizard's steps need to tag their Mixpanel events with the attempt they belong to.
 *
 * It lives in the wizard rather than in a step: applying a simulated planting date clears the
 * hybrids and sends the user back to the environment step, and the attempt must keep its id
 * across that.
 */
export type SeedRecommendationFlow = {
    /** Unset outside the wizard, where events go out without one. */
    flowId?: string;
    crop: 'corn' | 'soy';
    /** Unset outside the wizard, like `flowId`. */
    recommendationType?: SeedRecommendationType;
    /**
     * Planting-date simulations run since the last date was applied. A ref rather than state in
     * the simulator, which remounts on every tab switch and would restart the count.
     */
    simulations: { current: number };
};

export const SeedRecommendationFlowContext = createContext<SeedRecommendationFlow>({
    crop: 'corn',
    simulations: { current: 0 },
});

export const useSeedRecommendationFlow = () => useContext(SeedRecommendationFlowContext);

/** The properties every event of the attempt carries. */
export const flowEventProps = ({ flowId, crop, recommendationType }: SeedRecommendationFlow) => ({
    flow_id: flowId,
    crop,
    recommendation_type: recommendationType,
});

/** Days from the applied planting date to a simulated one; positive means a later sowing. */
export const daysFromOriginal = (original: string | undefined, date: string) =>
    original ? dayjs(date).diff(dayjs(original), 'day') : undefined;

/**
 * Reports a move between two wizard steps: forward completes the step being left, backward is
 * a step back. Indexes that fall outside `steps`, or no move at all, report nothing.
 */
export const trackStepChange = (
    flow: SeedRecommendationFlow,
    steps: SeedRecommendationStep[],
    fromIndex: number,
    toIndex: number,
    reason: 'manual' | 'planting_date_applied'
) => {
    const from = steps[fromIndex];
    const to = steps[toIndex];
    if (!from || !to) return;
    if (toIndex > fromIndex) {
        mpTrackEvent('Seed recommendation step completed', { ...flowEventProps(flow), step: from });
    } else if (toIndex < fromIndex) {
        mpTrackEvent('Seed recommendation step back', {
            ...flowEventProps(flow),
            from_step: from,
            to_step: to,
            reason,
        });
    }
};
